import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { downloadXlsx } from '@/lib/download';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const fmt = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;
const when = (s) => (s ? new Date(s).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }) : '');
const err = (e) => { const d = e.response?.data?.detail; return d?.message || (Array.isArray(d) ? 'Revisa los valores' : 'No se pudo guardar'); };

const Stat = ({ label, value, testid }) => (
  <div className="border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="text-xs font-semibold text-slate-500">{label}</div>
    <div className="mt-1 text-xl font-black text-slate-900">{value}</div>
  </div>
);

const SettingsForm = ({ settings, reload }) => {
  const [f, setF] = useState({ enabled: settings.enabled, min_purchase: settings.min_purchase ?? '', monthly_budget: settings.monthly_budget ?? '' });
  const save = async () => {
    try {
      await api.put('/admin/free-shipping-reserve/settings', { enabled: f.enabled, min_purchase: Number(f.min_purchase || 0),
        monthly_budget: f.monthly_budget === '' ? null : Number(f.monthly_budget) });
      toast.success('Promoción guardada'); reload();
    } catch (e) { toast.error(err(e)); }
  };
  return (
    <div className="border border-slate-200 bg-white p-5" data-testid="fsr-settings">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Configuración</div>
      <label className="mt-3 flex items-center gap-2 text-sm font-semibold">
        <input type="checkbox" checked={f.enabled} onChange={e => setF({ ...f, enabled: e.target.checked })} data-testid="fsr-enabled" /> Promoción activa
      </label>
      <div className="mt-3 flex flex-wrap gap-3">
        <label className="flex flex-col gap-1 text-xs font-semibold text-slate-700">Compra mínima por pedido (COP)
          <Input type="number" min="0" value={f.min_purchase} onChange={e => setF({ ...f, min_purchase: e.target.value })} className="rounded-none h-9 w-52" data-testid="fsr-min-purchase" />
        </label>
        <label className="flex flex-col gap-1 text-xs font-semibold text-slate-700">Presupuesto mensual de la reserva (vacío = sin tope)
          <Input type="number" min="0" value={f.monthly_budget} onChange={e => setF({ ...f, monthly_budget: e.target.value })} className="rounded-none h-9 w-52" data-testid="fsr-budget" />
        </label>
      </div>
      <Button onClick={save} className="mt-3 rounded-none bg-slate-900" data-testid="fsr-save">Guardar</Button>
    </div>
  );
};

const DepositForm = ({ reload }) => {
  const [f, setF] = useState({ amount: '', date: new Date().toISOString().slice(0, 10), note: '', initial: false });
  const save = async () => {
    if (!window.confirm(`¿Registrar ${fmt(f.amount)} en la reserva promocional?`)) return;
    try { await api.post('/admin/free-shipping-reserve/deposits', { ...f, amount: Number(f.amount) }); toast.success('Entrada registrada'); setF({ ...f, amount: '', note: '' }); reload(); }
    catch (e) { toast.error(err(e)); }
  };
  return (
    <div className="border border-slate-200 bg-white p-5" data-testid="fsr-deposit">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Registrar aporte o entrada</div>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <Input type="number" min="1" placeholder="Valor (COP)" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} className="rounded-none h-9 w-40" data-testid="fsr-deposit-amount" />
        <Input type="date" value={f.date} onChange={e => setF({ ...f, date: e.target.value })} className="rounded-none h-9 w-40" data-testid="fsr-deposit-date" />
        <Input placeholder="Observación" value={f.note} onChange={e => setF({ ...f, note: e.target.value })} className="rounded-none h-9 w-72" data-testid="fsr-deposit-note" />
        <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={f.initial} onChange={e => setF({ ...f, initial: e.target.checked })} data-testid="fsr-deposit-initial" /> Saldo inicial</label>
        <Button onClick={save} disabled={!f.amount || f.note.length < 3} className="rounded-none bg-emerald-700 h-9" data-testid="fsr-deposit-save">Registrar</Button>
      </div>
    </div>
  );
};

export default function AdminFreeShippingReserve() {
  const [d, setD] = useState(null);
  const load = () => api.get('/admin/free-shipping-reserve/summary').then(r => setD(r.data)).catch(() => toast.error('No se pudo cargar la reserva'));
  useEffect(() => { load(); }, []);
  if (!d) return <Layout><div className="p-10 text-sm text-slate-500">Cargando…</div></Layout>;
  const s = d.summary;
  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8 space-y-6" data-testid="fsr-page">
        <div>
          <Link to="/admin/configuracion/envios" className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Envíos y logística</Link>
          <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Envío gratis desde · reserva promocional</h1>
          <p className="mt-2 text-sm text-slate-600">Aplica a pedidos con envío gestionado por AutoMarket que superen la compra mínima. Se financia primero con el cargo logístico que ya paga el vendedor y la diferencia con esta reserva; si no alcanza, el comprador paga su envío y continúa. Esta reserva es independiente de los saldos de vendedores, de los desembolsos y de la utilidad de AutoMarket.</p>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="fsr-stats">
          <Stat label="Saldo inicial" value={fmt(s.initial)} testid="fsr-stat-initial" />
          <Stat label="Aportes y entradas" value={fmt(s.deposits)} testid="fsr-stat-deposits" />
          <Stat label="Subsidios utilizados" value={fmt(s.subsidies_used)} testid="fsr-stat-used" />
          <Stat label="Saldo disponible" value={fmt(s.available)} testid="fsr-stat-available" />
          <Stat label="Devuelto a la reserva" value={fmt(s.subsidies_released)} testid="fsr-stat-released" />
          <Stat label="Pedidos beneficiados" value={s.orders_benefited} testid="fsr-stat-orders" />
          <Stat label="Aporte de vendedores" value={fmt(s.seller_contributions)} testid="fsr-stat-sellers" />
          <Stat label="Usado este mes" value={fmt(s.month_used)} testid="fsr-stat-month" />
        </div>
        <SettingsForm settings={d.settings} reload={load} />
        <DepositForm reload={load} />
        <div className="border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Historial de movimientos</div>
            <Button variant="outline" className="rounded-none h-9" onClick={() => downloadXlsx('/admin/free-shipping-reserve/export', 'reserva_envio_gratis.xlsx')} data-testid="fsr-export">
              <Download className="h-4 w-4 mr-1" /> Exportar a Excel
            </Button>
          </div>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm" data-testid="fsr-movements">
              <thead className="bg-slate-50 text-left text-xs text-slate-500"><tr>
                <th className="p-2">Fecha</th><th className="p-2">Tipo</th><th className="p-2">Pedido</th><th className="p-2">Vendedor</th>
                <th className="p-2">Envío financiado</th><th className="p-2">Aporte vendedor</th><th className="p-2">Movimiento reserva</th><th className="p-2">Estado / observación</th>
              </tr></thead>
              <tbody>
                {d.movements.map(m => (
                  <tr key={m.entry_id} className="border-t border-slate-100" data-testid={`fsr-mov-${m.entry_id}`}>
                    <td className="p-2 text-xs">{when(m.at)}</td><td className="p-2 text-xs">{d.type_labels[m.type]}</td>
                    <td className="p-2 font-mono text-xs">{m.order_id || '—'}</td><td className="p-2 text-xs">{m.seller_name || '—'}</td>
                    <td className="p-2 text-xs">{m.full != null ? fmt(m.full) : '—'}</td><td className="p-2 text-xs">{m.seller_contribution != null ? fmt(m.seller_contribution) : '—'}</td>
                    <td className={`p-2 text-xs font-semibold ${m.amount < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{m.amount < 0 ? '−' : '+'}{fmt(Math.abs(m.amount))}</td>
                    <td className="p-2 text-xs">{d.status_labels[m.status] || ''} {m.note || ''} {m.by_name ? `· ${m.by_name}` : ''}</td>
                  </tr>
                ))}
                {!d.movements.length && <tr><td colSpan={8} className="p-3 text-slate-400">Sin movimientos.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
